import { INote } from '@dataTypes/note.type'

const TRAILING_VERSION = /\s-\sv(\d+)$/i

/**
 * Subset of note fields used to resolve version chains (list display, edit eligibility).
 * Shaped loosely so grid/draft rows (`NoteGridRow`, `Partial<INote>`) assign without casts.
 */
export type NoteVersionChainMember = {
  title?: string
  version?: number
  is_latest?: boolean
  note_group_id?: string
  patient_id?: string | null
  contact_id?: string | null
  /** API uses `INote['context_type']`; grid rows may use a plain string. */
  context_type?: INote['context_type'] | string
  context_id?: string | null
}

/**
 * Removes a trailing " - vN" suffix (our version convention) for grouping titles.
 */
export function stripNoteVersionSuffix(title: string): string {
  return title.replace(TRAILING_VERSION, '').trim()
}

/**
 * Numeric version from title suffix, or 0 when there is no trailing " - vN".
 */
export function noteTitleEffectiveVersion(title: string): number {
  const m = TRAILING_VERSION.exec(title.trim())
  if (!m) return 0
  return parseInt(m[1], 10)
}

export function noteEffectiveVersion(
  note: Pick<INote, 'version'> & { title?: string },
): number {
  if (typeof note.version === 'number' && Number.isFinite(note.version)) {
    return note.version
  }
  return noteTitleEffectiveVersion(note.title ?? '')
}

function ctxKey(value: string | null | undefined): string {
  return (value ?? '').trim()
}

/**
 * True when two notes belong to the same patient/contact + context (version chain must not cross rows).
 */
export function notesShareVersionContext(
  a: NoteVersionChainMember,
  b: NoteVersionChainMember,
): boolean {
  return (
    ctxKey(a.patient_id) === ctxKey(b.patient_id) &&
    ctxKey(a.contact_id) === ctxKey(b.contact_id) &&
    a.context_type === b.context_type &&
    ctxKey(a.context_id) === ctxKey(b.context_id)
  )
}

/**
 * All notes in `pool` that share the version context and base title stem with `note`.
 */
export function getVersionChainMembers(
  note: NoteVersionChainMember,
  pool: NoteVersionChainMember[],
): NoteVersionChainMember[] {
  const noteGroupId = note.note_group_id?.trim()
  if (noteGroupId) {
    return pool.filter((n) => (n.note_group_id ?? '').trim() === noteGroupId)
  }
  const stem = stripNoteVersionSuffix(note.title ?? '')
  return pool.filter(
    (n) =>
      notesShareVersionContext(note, n) &&
      stripNoteVersionSuffix(n.title ?? '') === stem,
  )
}

/**
 * Highest trailing version number among chain members (0 if none use " - vN").
 */
export function maxVersionInChain(
  note: NoteVersionChainMember,
  pool: NoteVersionChainMember[],
): number {
  const members = getVersionChainMembers(note, pool)
  if (members.length === 0) return noteEffectiveVersion(note)
  return Math.max(...members.map((m) => noteEffectiveVersion(m)), 0)
}

function computeIsLatestInVersionChain(
  note: NoteVersionChainMember,
  pool: NoteVersionChainMember[],
): boolean {
  const members = getVersionChainMembers(note, pool)
  if (members.length === 0) return true
  const maxV = maxVersionInChain(note, pool)
  // Multiple rows in the same chain: derive "latest" from version numbers so stale
  // `is_latest` on local state (e.g. after a new version is appended) does not keep
  // older rows looking like the current version or hide historical " - v1" labels.
  if (members.length > 1) {
    return noteEffectiveVersion(note) === maxV
  }
  if (typeof note.is_latest === 'boolean') {
    return note.is_latest
  }
  return noteEffectiveVersion(note) === maxV
}

export type FormatNoteTitleWithVersionOptions = {
  /**
   * When provided, the function can determine whether this note is the latest one
   * in its version chain (so we can decide whether to show " - vN").
   */
  versionChainPool?: NoteVersionChainMember[]
}

export function formatNoteTitleWithVersion(
  note: Pick<INote, 'title' | 'version'> & Partial<NoteVersionChainMember>,
  options?: FormatNoteTitleWithVersionOptions,
): string {
  const rawTitle = (note.title ?? '').trim()
  const baseTitle =
    rawTitle.length > 0 ? stripNoteVersionSuffix(rawTitle) : 'Untitled Note'
  const version = noteEffectiveVersion(note)
  const pool = options?.versionChainPool
  if (pool != null && pool.length > 0) {
    const chainNote = note as NoteVersionChainMember
    const isLatestInChain = computeIsLatestInVersionChain(chainNote, pool)
    if (isLatestInChain && version === 1) {
      // Hide the suffix only when the *latest* note is version 1.
      // For versions > 1, show " - vN" even for the latest row.
      return baseTitle
    }
  }
  if (version > 0) {
    return `${baseTitle} - v${version}`
  }
  return baseTitle
}

/**
 * True when this note belongs to a chain with more than one saved version.
 */
export function hasMultipleVersionsInChain(
  note: NoteVersionChainMember,
  pool: NoteVersionChainMember[],
): boolean {
  // Ensure historical versions are explicitly labeled (e.g. "- v1").
  if (note.is_latest === false) return true

  const noteGroupId = note.note_group_id?.trim()
  if (noteGroupId) {
    const sameGroupCount = pool.filter(
      (n) => (n.note_group_id ?? '').trim() === noteGroupId,
    ).length
    if (sameGroupCount > 1) return true
    return noteEffectiveVersion(note) > 1
  }

  const members = getVersionChainMembers(note, pool)
  if (members.length > 1) return true
  return noteEffectiveVersion(note) > 1
}

/**
 * Only the note with the highest " - vN" (or the sole unsuffixed note) is editable.
 */
export function isLatestNoteInVersionChain(
  note: NoteVersionChainMember,
  pool: NoteVersionChainMember[],
): boolean {
  return computeIsLatestInVersionChain(note, pool)
}
